import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { Info, RotateCcw } from 'lucide-react'
import { ModalShell } from '../common/ModalShell.js'
import { getServerNowMs } from '../../hooks/useServerClock.js'
import { itemsApi } from '../../lib/api.js'
import { scheduleItemTitle } from '../../lib/utils.js'
import {
  LIVE_PART_MIN_SEC,
  liveBlockCarrier,
  liveBlockRundown,
  withLivePartAdjustments,
  type LivePartSegment,
} from '../../lib/livePartPlan.js'
import {
  formatMinSec,
  liveBlockFrieze,
  livePartRowState,
  parseMinSec,
  type LivePartRowState,
} from '../../lib/livePartAdjust.js'
import { useStore } from '../../store/index.js'
import type { ScheduleItem } from '../../types/index.js'
import './LivePartChrono.css'

/**
 * Ajustement antenne du déroulé estimé, ouvert depuis le chrono de partie
 * (maquette `designs/ajustement-parties.pen`).
 *
 * En tête, le bloc : ce qu'il reste, l'heure, la fin, et la frise des parties à
 * l'échelle — coupures en creux — avec le repère de l'instant. Dessous, une
 * ligne par partie : terminée (lecture seule, durée réelle), en cours, au
 * raccrochage ou à venir. Chaque partie modifiable a une seule saisie « mm:ss »
 * et un choix Auto / Forcé.
 *
 * En auto, une partie garde sa durée annoncée et la dernière s'étend jusqu'à
 * la fin du bloc, recalculée à chaque décrochage. Forcée, elle tient la durée
 * saisie. C'est une annotation de conduite : la durée annoncée, donc l'EPG, la
 * grille et le playout n'en lisent rien ; seuls le chrono et la Clock suivent.
 */

/** Plafond miroir de `LIVE_PART_ON_AIR_MAX_SEC`. */
const ON_AIR_MAX_SEC = 6 * 3600

interface PartDraft {
  auto: boolean
  /** Texte du champ quand la partie est forcée. */
  text: string
}

const STATE_LABEL: Record<LivePartRowState, string> = {
  DONE:     'Terminée',
  ON_AIR:   'En cours',
  RESUME:   'Au raccrochage',
  UPCOMING: 'À venir',
}

function clockAt(ms: number): string {
  return new Date(ms).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
}

function partIndexOf(segment: LivePartSegment): number {
  return segment.partIndex ?? segment.number - 1
}

/** Durée saisie valide, sinon `null`. */
function forcedSec(draft: PartDraft): number | null {
  const sec = parseMinSec(draft.text)
  return sec != null && sec >= LIVE_PART_MIN_SEC && sec <= ON_AIR_MAX_SEC ? sec : null
}

interface LivePartAdjustDialogProps {
  item:    ScheduleItem
  items:   readonly ScheduleItem[]
  onClose: () => void
}

export const LivePartAdjustDialog = memo(function LivePartAdjustDialog({ item, items, onClose }: LivePartAdjustDialogProps) {
  const [now, setNow] = useState(getServerNowMs)
  useEffect(() => {
    const timer = setInterval(() => setNow(getServerNowMs()), 1000)
    return () => clearInterval(timer)
  }, [])

  // Lecture à l'ouverture : brouillons, et parties encore modifiables. Figées
  // pour que la liste ne change pas sous les doigts de l'opérateur si une
  // partie s'achève pendant la saisie ; une partie terminée garde son réglage.
  const [opening] = useState(() => {
    const rundown = liveBlockRundown(item, items, getServerNowMs())
    const segments = rundown?.segments ?? []
    return {
      drafts: Object.fromEntries(segments.map(segment => [partIndexOf(segment), {
        auto: segment.adjustedSec == null,
        text: formatMinSec(segment.adjustedSec ?? segment.plannedSec),
      }])) as Record<number, PartDraft>,
      editable: new Set(segments
        .filter(segment => rundown && livePartRowState(rundown, segment) !== 'DONE')
        .map(partIndexOf)),
    }
  })
  const { editable } = opening
  const [drafts, setDrafts] = useState<Record<number, PartDraft>>(opening.drafts)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  // Ce qui part au serveur : `null` pour auto, la durée sinon. Une saisie
  // invalide reste hors de l'aperçu et bloque « Appliquer ».
  const adjustments = useMemo(() => {
    const entries: Array<[number, number | null]> = []
    for (const partIndex of editable) {
      const draft = drafts[partIndex]
      if (!draft || draft.auto) entries.push([partIndex, null])
      else if (forcedSec(draft) != null) entries.push([partIndex, forcedSec(draft)])
    }
    return Object.fromEntries(entries) as Record<number, number | null>
  }, [drafts, editable])
  const invalid = [...editable].some(partIndex => {
    const draft = drafts[partIndex]
    return Boolean(draft && !draft.auto && forcedSec(draft) == null)
  })

  // Aperçu : le déroulé tel qu'il sera une fois l'ajustement appliqué.
  const preview = useMemo(
    () => liveBlockRundown(item, withLivePartAdjustments(item, items, adjustments), now),
    [item, items, adjustments, now],
  )
  const frieze = useMemo(() => preview ? liveBlockFrieze(preview, now) : null, [preview, now])
  const remainingSec = preview ? Math.max(0, (preview.blockEndMs - now) / 1000) : 0
  const subtitle = [scheduleItemTitle(item, 'Direct'), item.liveSource?.name].filter(Boolean).join(' — ')

  const setMode = useCallback((partIndex: number, auto: boolean, currentSec: number) => {
    setDrafts(current => ({
      ...current,
      // Passer en forcé part de la durée affichée : l'opérateur ajuste, il ne
      // repart pas de zéro.
      [partIndex]: auto ? { ...current[partIndex], auto: true } : { auto: false, text: formatMinSec(currentSec) },
    }))
  }, [])

  const setText = useCallback((partIndex: number, text: string) => {
    setDrafts(current => ({ ...current, [partIndex]: { auto: false, text } }))
  }, [])

  const resetAll = useCallback(() => {
    setDrafts(current => Object.fromEntries(Object.entries(current)
      .map(([index, draft]) => [index, editable.has(Number(index)) ? { ...draft, auto: true } : draft])))
  }, [editable])

  const apply = useCallback(async () => {
    const carrier = liveBlockCarrier(item, items)
    if (!carrier) return
    setBusy(true)
    setError('')
    try {
      const saved = await itemsApi.setLivePartsOnAir(carrier.id, Object.entries(adjustments)
        .map(([partIndex, durationSec]) => ({ partIndex: Number(partIndex), durationSec })))
      // Seul le déroulé est repris : `GRID_UPDATED` rechargera le reste.
      useStore.getState().upsertItem({ ...carrier, liveParts: saved.liveParts })
      onClose()
    } catch (cause: unknown) {
      setError(cause instanceof Error ? cause.message : 'Impossible d’ajuster le déroulé.')
    } finally {
      setBusy(false)
    }
  }, [adjustments, item, items, onClose])

  return (
    <ModalShell
      title="Ajuster les parties du direct"
      className="live-part-adjust-modal"
      onClose={onClose}
      closeDisabled={busy}
    >
      <div className="tp-modal__body live-part-adjust">
        {subtitle && <p className="live-part-adjust__subtitle">{subtitle}</p>}

        {preview && frieze && (
          <section className="live-part-adjust__block" aria-label="Bloc de direct">
            <div className="live-part-adjust__block-top">
              <div className="live-part-adjust__figure">
                <span className="live-part-adjust__figure-label">Reste du bloc</span>
                <strong className="live-part-adjust__remaining">{formatMinSec(remainingSec)}</strong>
              </div>
              <div className="live-part-adjust__marks">
                <div className="live-part-adjust__figure">
                  <span className="live-part-adjust__figure-label">Il est</span>
                  <span className="live-part-adjust__mark">{clockAt(now)}</span>
                </div>
                <div className="live-part-adjust__figure">
                  <span className="live-part-adjust__figure-label">Fin du bloc</span>
                  <span className="live-part-adjust__mark">{clockAt(preview.blockEndMs)}</span>
                </div>
              </div>
            </div>

            <div className="live-part-adjust__frieze" aria-hidden="true">
              {frieze.cells.map(cell => (
                <span
                  key={cell.number}
                  className="live-part-adjust__frieze-cell"
                  data-state={cell.state.toLowerCase()}
                  style={{ left: `${cell.leftPct}%`, width: `${cell.widthPct}%` }}
                >
                  {cell.state === 'ON_AIR' && (
                    <span className="live-part-adjust__frieze-elapsed" style={{ width: `${cell.elapsed * 100}%` }} />
                  )}
                </span>
              ))}
              {frieze.nowPct != null && (
                <span className="live-part-adjust__playhead" style={{ left: `${frieze.nowPct}%` }} />
              )}
            </div>

            <ul className="live-part-adjust__legend">
              {preview.segments.map(segment => (
                <li key={segment.number} data-state={livePartRowState(preview, segment).toLowerCase()}>
                  {segment.number} — {segment.title ?? `Partie ${segment.number}`}
                </li>
              ))}
            </ul>
          </section>
        )}

        <p className="live-part-adjust__help">
          En auto, une partie garde sa durée annoncée et la dernière s’étend jusqu’à la fin du bloc.
          Ces durées ne concernent que la régie et la Clock : l’EPG ne change pas.
        </p>

        {!preview || preview.segments.length === 0 ? (
          <p className="live-part-adjust__help">Aucune partie à ajuster.</p>
        ) : (
          <ol className="live-part-adjust__list">
            {preview.segments.map(segment => {
              const partIndex = partIndexOf(segment)
              const state = livePartRowState(preview, segment)
              const readOnly = !editable.has(partIndex) || state === 'DONE'
              const draft = drafts[partIndex] ?? { auto: true, text: formatMinSec(segment.plannedSec) }
              const forced = readOnly || draft.auto ? null : forcedSec(draft)
              const bad = !readOnly && !draft.auto && forced == null
              const inputId = `live-part-adjust-${partIndex}`
              return (
                <li
                  key={segment.number}
                  className="live-part-adjust__row"
                  data-state={state.toLowerCase()}
                  data-auto={(!readOnly && draft.auto) || undefined}
                >
                  <span className="live-part-adjust__accent" aria-hidden="true" />
                  <div className="live-part-adjust__info">
                    <div className="live-part-adjust__head">
                      <strong>Partie {segment.number}{segment.title ? ` — ${segment.title}` : ''}</strong>
                      <span className="live-part-adjust__status">{STATE_LABEL[state]}</span>
                    </div>
                    <div className="live-part-adjust__meta">
                      {segment.announcedSec != null && <span>annoncée {formatMinSec(segment.announcedSec)}</span>}
                      <span className="live-part-adjust__range">
                        {state === 'UPCOMING' || state === 'RESUME' ? '≈ ' : ''}{clockAt(segment.startMs)} → {clockAt(segment.endMs)}
                      </span>
                      {forced != null && <span>forcée à {formatMinSec(forced)}</span>}
                      {!readOnly && draft.auto && segment.absorbs && <span>jusqu’à la fin du bloc</span>}
                    </div>
                    {bad && (
                      <span className="live-part-adjust__warning" role="alert">
                        Durée en mm:ss, au moins {LIVE_PART_MIN_SEC} s
                      </span>
                    )}
                  </div>

                  <div className="live-part-adjust__control">
                    <label className="sr-only" htmlFor={inputId}>Durée de la partie {segment.number}</label>
                    <input
                      id={inputId}
                      className="live-part-adjust__duration"
                      inputMode="numeric"
                      autoComplete="off"
                      spellCheck={false}
                      value={readOnly ? formatMinSec(segment.durationSec) : draft.auto ? formatMinSec(segment.plannedSec) : draft.text}
                      readOnly={readOnly}
                      disabled={busy}
                      aria-invalid={bad || undefined}
                      onChange={event => setText(partIndex, event.target.value)}
                      onFocus={event => { if (!readOnly) event.target.select() }}
                    />
                    {readOnly ? (
                      <span className="live-part-adjust__frozen">durée réelle</span>
                    ) : (
                      <div className="live-part-adjust__mode" role="group" aria-label={`Mode de la partie ${segment.number}`}>
                        <button
                          type="button"
                          aria-pressed={draft.auto}
                          disabled={busy}
                          onClick={() => setMode(partIndex, true, segment.plannedSec)}
                        >
                          Auto
                        </button>
                        <button
                          type="button"
                          aria-pressed={!draft.auto}
                          disabled={busy}
                          onClick={() => setMode(partIndex, false, segment.plannedSec)}
                        >
                          Forcé
                        </button>
                      </div>
                    )}
                  </div>
                </li>
              )
            })}
          </ol>
        )}

        {error && <div className="agenda-editor__notice is-danger" role="alert">⚠ {error}</div>}
      </div>

      <footer className="tp-modal__footer live-part-adjust__footer">
        <p className="live-part-adjust__note">
          <Info size={14} aria-hidden="true" />
          La Clock et la régie suivent immédiatement. L’EPG reste inchangé.
        </p>
        <div className="live-part-adjust__actions">
          <button type="button" className="tp-button" onClick={resetAll} disabled={busy || editable.size === 0}>
            <RotateCcw size={14} aria-hidden="true" />Tout remettre en auto
          </button>
          <button
            type="button"
            className="tp-button tp-button--primary"
            onClick={() => void apply()}
            disabled={busy || invalid || editable.size === 0}
          >
            {busy ? 'Application…' : 'Appliquer'}
          </button>
        </div>
      </footer>
    </ModalShell>
  )
})
